import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

import { AppConfig } from "src/config/AppConfig";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Loader from "src/app/components/Loader/Loader";
import AudienceOverlapResult from "src/app/components/AudienceOverlap/AudienceOverlapResult";
import { ReactComponent as LoaderSpinner } from "src/images/loader-dots.svg";
import { ReactComponent as DownloadIcon } from "src/images/xlsx.svg";
import { ReactComponent as RubberIcon } from "src/images/rubber.svg";
import { Button } from "src/app/components/Button/Button";
import { convertToLocalTimezone } from "src/app/methods/convertToLocalTimezone";
import { showToast } from "src/app/methods/showToast";
import { usePrevious } from "src/app/methods/usePrevious";
import { useDispatch, useSelector } from "react-redux";
import { getProjectAudienceOverlap } from "src/redux";
import { RootState } from "src/redux/reducers";
import { ProjectPermissionsEnum } from "src/utils/PermissionsEnums";
import { getCampaignAudienceOverlapResults } from "./utils";
import { EmptyAudienceOverlapTab } from "./components/EmptyAudienceOverlap/EmptyAudienceOverlapTab";
import { SelectCreatorsForAOModal } from "./components/SelectCreatorsForAOModal/SelectCreatorsForAOModal";
import ClearAudienceOverlapModal from "./components/ClearAudienceOverlapModal/ClearAudienceOverlapModal";

import "./AudienceOverlapTab.scss";

export const AudienceOverlapTab = () => {
  const [showAddCreatorsModal, setShowAddCreatorsModal] = useState(false);
  const [showClearAOModal, setShowClearAOModal] = useState(false);

  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();

  const {
    audienceOverlap: data,
    audienceOverlapLoading: loading,
    audienceOverlapCalculating: isCalculating,
    projectBasicData,
  } = useSelector((state: RootState) => state.projectReducer);

  const isClientOrSeeAsClient =
    projectBasicData?.seeAsClientMode ||
    projectBasicData?.isCurrentMemberClient;

  useEffect(() => {
    dispatch(getProjectAudienceOverlap(projectId));
  }, [projectId, dispatch]);

  const wasCalculating = usePrevious(isCalculating);
  const hasAnyResult = Object.values(data?.audienceOverlapData || {}).some(
    (providerData) => providerData.audienceOverlap?.data?.length,
  );

  // The analysis has just finished without any result: without a message it looks like nothing happened
  useEffect(() => {
    if (!wasCalculating || isCalculating || loading || hasAnyResult) return;

    showToast(
      "error",
      <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
      <IDHFormattedMessage
        id="ws_audience_overlap_not_enough_accounts_with_data"
        defaultMessage="Audience overlap needs at least two creators with audience data on the same platform."
      />,
    );
  }, [wasCalculating, isCalculating, loading, hasAnyResult]);

  const results = useMemo(
    () =>
      data?.audienceOverlapData
        ? getCampaignAudienceOverlapResults(data.audienceOverlapData)
        : [],
    [data?.audienceOverlapData],
  );

  const renderSelectCreatorsForAOModal = () => {
    if (showAddCreatorsModal) {
      return (
        <SelectCreatorsForAOModal
          onClose={() => setShowAddCreatorsModal(false)}
        />
      );
    }
    return null;
  };

  if (loading) return <Loader />;

  if (!results.length && !isCalculating) {
    return (
      <>
        <EmptyAudienceOverlapTab
          openAddCreatorsModal={() => setShowAddCreatorsModal(true)}
        />
        {renderSelectCreatorsForAOModal()}
      </>
    );
  }

  const renderContent = () => {
    if (isCalculating)
      return (
        <div className="audience-overlap-tab__spinner">
          <LoaderSpinner />
          <span className="audience-overlap-tab__spinner-text">
            <IDHFormattedMessage
              id="ws_please_wait_patiently"
              defaultMessage="Please wait patiently"
            />
          </span>
          <span className="audience-overlap-tab__spinner-description">
            <IDHFormattedMessage
              id="ws_we_are_gathering_data_for_this_account"
              defaultMessage="We are gathering data for this account.
It will be available within a few minutes."
            />
          </span>
        </div>
      );

    // every new analysis starts from the first platform, as before
    return (
      <AudienceOverlapResult
        key={data?.audienceOverlapLastSync?.date}
        results={results}
      />
    );
  };

  const { audienceOverlapData, audienceOverlapLastSync } = data || {};
  const hasDataForAnyPlatform = Object.values(audienceOverlapData || {}).some(
    (v) => v.audienceOverlap?.data,
  );

  return (
    <div className="audience-overlap-tab">
      <div className="tab-filter">
        <div className="tab-filter__header">
          <IDHFormattedMessage
            id="ws_audience_overlap_tab_text"
            defaultMessage="Percentage of overlapping and unique followers, both number and percentage-wise across all followers of Creators in campaign."
          />
        </div>

        <div className="audience-overlap-tab__analysis">
          {projectBasicData?.permissions?.project.includes(
            ProjectPermissionsEnum.CALCULATE_PROJECT_AUDIENCE_OVERLAP,
          ) && (
            <Button
              variant="blue"
              size="medium"
              onClick={() => setShowAddCreatorsModal(true)}
              dataQa="new-analysis-button"
              disabled={isCalculating}
            >
              <IDHFormattedMessage
                id="ws_new_analysis"
                defaultMessage="New analysis"
              />
            </Button>
          )}

          <div className="audience-overlap-tab__analysis-date">
            <span className="audience-overlap-tab__analysis-date-label">
              <IDHFormattedMessage
                id="ws_last_analysis_date"
                defaultMessage="Last analysis date"
              />
            </span>
            <span className="audience-overlap-tab__analysis-date-value">
              {audienceOverlapLastSync
                ? convertToLocalTimezone(audienceOverlapLastSync.date)
                    .format("DD.MM.YYYY")
                    .toString()
                : "-"}
            </span>
          </div>

          <div className="audience-overlap-tab__buttons">
            {hasDataForAnyPlatform && (
              <Button
                variant="white"
                size="medium"
                dataQa="download-xlsx-button"
                onClick={() => {
                  window.location.href = `${AppConfig.getApiRoute()}/project/audience-overlap-xlsx-report/${projectId}`;
                }}
                disabled={isCalculating}
              >
                <DownloadIcon />
                <IDHFormattedMessage
                  id="ws_download_xlsx"
                  defaultMessage="Download XLSX."
                />
              </Button>
            )}

            {!isClientOrSeeAsClient && (
              <Button
                variant="white"
                size="medium"
                dataQa="clear-analysis-button"
                onClick={() => setShowClearAOModal(true)}
              >
                <RubberIcon />
                <IDHFormattedMessage
                  id="ws_clear_analysis"
                  defaultMessage="Clear analysis"
                />
              </Button>
            )}
          </div>
        </div>

        {renderContent()}

        {renderSelectCreatorsForAOModal()}
        {showClearAOModal && (
          <ClearAudienceOverlapModal
            onClose={() => setShowClearAOModal(false)}
          />
        )}
      </div>
    </div>
  );
};
